<template>
    <!-- Teleport porque a barra lateral que hospeda este componente é um flex item com
         `z-index`, o que cria contexto de empilhamento: o diálogo ficaria preso nesse
         nível enquanto o backdrop que o Bootstrap injeta no `body` pintaria por cima. -->
    <Teleport to="body">
    <div class="modal fade" ref="modalRef" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content border-0 shadow-lg">

                <div class="modal-header">
                    <div class="d-flex align-items-center gap-2">
                        <div class="comunidade-avatar">
                            <img v-if="comunidade?.imagem && !imagemFalhou"
                                :src="comunidade.imagem"
                                :alt="comunidade.nome"
                                @error="imagemFalhou = true" />
                            <i v-else class="ri-group-2-fill"></i>
                        </div>
                        <div>
                            <h5 class="modal-title mb-0">{{ comunidade?.nome || 'Comunidade' }}</h5>
                            <p class="text-muted font-size-12 mb-0">Comunidade do WhatsApp</p>
                        </div>
                    </div>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>

                <div class="modal-body">
                    <div v-if="carregando" class="text-center py-4 text-muted">
                        <i class="ri-loader-4-line spin font-size-20"></i>
                        <p class="mb-0 mt-2 font-size-13">Carregando comunidade...</p>
                    </div>

                    <div v-else-if="erro" class="text-center py-4">
                        <i class="ri-error-warning-line font-size-24 text-warning"></i>
                        <p class="mb-0 mt-2 font-size-13 text-muted">{{ erro }}</p>
                    </div>

                    <template v-else-if="comunidade">
                        <div v-if="comunidade.descricao" class="mb-3">
                            <h6 class="font-size-13 text-muted mb-1">Descrição</h6>
                            <p class="mb-0 font-size-14" style="white-space: pre-wrap;">{{ comunidade.descricao }}</p>
                        </div>

                        <h6 class="font-size-13 text-muted mb-2">
                            Grupos desta comunidade neste inbox
                            <span class="badge bg-light text-dark ms-1">{{ comunidade.subgrupos.length }}</span>
                        </h6>

                        <!-- A lista é o que ESTE inbox alcança e este usuário pode ver, não a
                             comunidade inteira: grupo restrito fora da whitelist não entra. Dizer
                             isso evita que a contagem seja lida como o tamanho real da comunidade. -->
                        <p class="text-muted font-size-11 mb-2">
                            <i class="ri-information-line me-1"></i>
                            Apenas os grupos que este inbox participa e que você tem permissão de ver.
                        </p>

                        <div v-if="comunidade.subgrupos.length === 0" class="text-muted font-size-13 py-2">
                            Nenhum grupo visível nesta comunidade.
                        </div>

                        <ul v-else class="list-unstyled mb-0 subgrupo-lista">
                            <li v-for="sg in comunidade.subgrupos" :key="sg.id"
                                class="subgrupo-item"
                                :class="{ 'subgrupo-atual': sg.ehGrupoAtual }"
                                @click="abrirSubgrupo(sg)">
                                <div class="subgrupo-avatar">
                                    <!-- Mesmo tratamento do avatar da comunidade: URL da
                                         plataforma expira, e sem `@error` viraria ícone de
                                         imagem quebrada em vez do fallback. -->
                                    <img v-if="sg.imagem && !avataresFalhos.has(sg.id)"
                                        :src="sg.imagem"
                                        :alt="sg.nome"
                                        @error="avataresFalhos.add(sg.id)" />
                                    <i v-else class="ri-group-fill"></i>
                                </div>
                                <div class="flex-grow-1 overflow-hidden">
                                    <div class="d-flex align-items-center gap-1">
                                        <span class="text-truncate font-size-14">{{ sg.nome }}</span>
                                        <span v-if="sg.ehGrupoAvisos"
                                            class="badge bg-primary-subtle text-primary font-size-10">Avisos</span>
                                        <span v-if="sg.somenteAdmins"
                                            class="badge bg-warning-subtle text-warning font-size-10">Só admin</span>
                                    </div>
                                    <span v-if="sg.ehGrupoAtual" class="text-muted font-size-11">Grupo atual</span>
                                </div>
                                <i v-if="!sg.ehGrupoAtual" class="ri-arrow-right-s-line text-muted"></i>
                            </li>
                        </ul>
                    </template>
                </div>

            </div>
        </div>
    </div>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted } from 'vue';
import { Modal } from 'bootstrap';
import { GrupoService, ComunidadeDetalhe, ComunidadeSubgrupo } from '@/services/grupos/grupos.service';
import { useChatStore } from '@/stores/app/chatStore';
import { toast } from 'vue3-toastify';
import { IdGrupo } from '@/services/common-types';

const chatStore = useChatStore();

const modalRef = ref<HTMLElement | null>(null);
let modal: Modal | null = null;

const comunidade = ref<ComunidadeDetalhe | null>(null);
const carregando = ref(false);
const erro = ref<string | null>(null);
/** A foto vem de URL da plataforma e pode expirar; sem isto vira imagem quebrada. */
const imagemFalhou = ref(false);
/** Mesmo motivo, por subgrupo. `reactive` para o `add` disparar re-render. */
const avataresFalhos = reactive(new Set<string>());

onMounted(() => {
    if (modalRef.value) modal = new Modal(modalRef.value);
});

// Desmontar no meio de um `hide()` deixa o backdrop e o `body.modal-open` órfãos —
// tela escura, cliques bloqueados, scroll travado, sem nada na tela para fechar. E
// isso acontece: escolher um subgrupo em aba Kanban abre o chat em modal, o que
// desmonta a barra lateral que hospeda este componente durante a animação de fechar.
onUnmounted(() => {
    modal?.hide();
    modal?.dispose();
});

async function abrir(idGrupo: IdGrupo) {
    // Estado zerado a cada abertura: o modal é reaproveitado entre grupos, e sobra do
    // anterior apareceria por um instante antes da nova resposta.
    comunidade.value = null;
    erro.value = null;
    imagemFalhou.value = false;
    avataresFalhos.clear();
    carregando.value = true;
    modal?.show();

    try {
        comunidade.value = await new GrupoService().getComunidade(idGrupo);
    } catch (e) {
        console.error('[ComunidadeModal] getComunidade falhou:', e);
        erro.value = 'Não foi possível carregar a comunidade deste grupo.';
    } finally {
        carregando.value = false;
    }
}

function abrirSubgrupo(sg: ComunidadeSubgrupo) {
    if (sg.ehGrupoAtual) return;

    // A comunidade vem inteira do servidor, mas a lista de conversas do front é
    // paginada — e também esconde grupo restrito. `selectChat` com um id ausente da
    // lista deixa `activeChatId` apontando para nada: o getter `activeChat` devolve
    // null, a busca de mensagens cai no ramo de contato, lança por falta de
    // `idContato`, e `isLoadingMessages` fica preso em true. Spinner eterno, só sai
    // com recarregar a página.
    if (!chatStore.chatList.some(c => c.id === sg.id)) {
        toast.info('Este grupo ainda não está carregado na sua lista de conversas. Role a lista para carregá-lo.');
        return;
    }

    modal?.hide();
    // `.catch` porque `selectChat` é async e pode rejeitar; sem ele a rejeição fica
    // sem tratamento e o estado de carregamento não se resolve.
    void chatStore.selectChat(sg.id).catch(e => console.error('[ComunidadeModal] selectChat falhou:', e));
}

defineExpose({ abrir });
</script>

<style scoped>
.comunidade-avatar,
.subgrupo-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background-color: var(--bs-light);
    color: #6c757d;
    flex-shrink: 0;
}

/* Cantos arredondados, não círculo: é como a comunidade se distingue de um grupo na
   própria interface do WhatsApp. */
.comunidade-avatar {
    width: 40px;
    height: 40px;
    border-radius: 12px;
}

.comunidade-avatar img,
.subgrupo-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.subgrupo-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
}

.subgrupo-lista {
    max-height: 320px;
    overflow-y: auto;
}

.subgrupo-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    cursor: pointer;
}

.subgrupo-item:hover {
    background-color: var(--bs-light);
}

.subgrupo-item.subgrupo-atual {
    background-color: var(--bs-primary-bg-subtle);
    cursor: default;
}

.spin {
    display: inline-block;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

[data-bs-theme=dark] .comunidade-avatar,
[data-bs-theme=dark] .subgrupo-avatar {
    background-color: rgba(255, 255, 255, 0.06);
}

[data-bs-theme=dark] .subgrupo-item:hover {
    background-color: rgba(255, 255, 255, 0.04);
}
</style>
